'use client'

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { AuthProvider, useAuthPageContext } from '@/features/auth/auth-context'
import { AuthSteps, getAuthStepHeaderPropsByStep } from '@/features/auth/auth-steps'
import { AUTH_STEPS } from '@/features/auth/helpers/auth-constants'
import { useLandingAuthContext } from '@/features/landing-page/landing-auth-context'
import { GeneralModalContent } from '@/components/ui/general-modal'
import { cn } from '@/utils/styles/cn'
import { useParams, usePathname, useRouter } from 'next/navigation'
import { UrlConfig } from '@/setup/routing/routing'
import { useMemo } from 'react'
import Image from 'next/image'

const AUTH_ICON = (
    <Image
        src="/assets/landing-v3/superscale-app-icon.svg"
        alt="Superscale app icon"
        width={44}
        height={44}
        className="h-11 w-11"
    />
)

export const LandingAuthContent = () => {
    const pathname = usePathname()
    const router = useRouter()
    const params = useParams()
    const { isAuthModalOpen, closeAuthModal, initialAuthStep, boardReferralCode } = useLandingAuthContext()

    const isAuthRoute = pathname.includes(UrlConfig['/auth'].getUrl())
    const isAuthCodeVerification = isAuthRoute && params?.auth_step === AUTH_STEPS.CodeVerification

    const isOpen = isAuthModalOpen || isAuthRoute
    const blockClose = isAuthCodeVerification

    return (
        <Dialog
            key="auth-dialog"
            open={isOpen}
            onOpenChange={(open) => {
                if (blockClose) return
                if (!open) {
                    closeAuthModal()
                    if (isAuthRoute) {
                        router.replace('/', { scroll: false })
                    }
                }
            }}
        >
            <DialogContent
                withoutCloseButton
                fullScreenOnMobile
                hideMobileLogo
                className={cn(
                    'w-[calc(100vw-32px)] flex justify-center overflow-hidden p-4 max-md:px-0 max-md:pb-0 max-md:border-0',
                    'max-md:overflow-y-hidden',
                    'md:border-0 md:bg-transparent md:shadow-none md:p-0 md:gap-0 md:rounded-none md:overflow-visible md:w-auto md:max-w-fit'
                )}
                onInteractOutside={(e) => {
                    if (blockClose) e.preventDefault()
                }}
            >
                <AuthProvider initialStep={initialAuthStep} boardReferralCode={boardReferralCode}>
                    <InnerAuthContent blockClose={blockClose} />
                </AuthProvider>
            </DialogContent>
        </Dialog>
    )
}

const InnerAuthContent = ({ blockClose }: { blockClose: boolean }) => {
    const { currentStep } = useAuthPageContext()

    const modalHeaderProps = useMemo(() => {
        const header = getAuthStepHeaderPropsByStep(currentStep)
        return { title: header.title, subtitle: header.subtitle, icon: AUTH_ICON }
    }, [currentStep])

    return (
        <div className="max-md:flex-1 max-md:min-h-0 max-md:flex max-md:flex-col">
            <DialogTitle className="sr-only">Authentication</DialogTitle>
            <GeneralModalContent
                title={modalHeaderProps.title}
                subtitle={modalHeaderProps.subtitle}
                icon={modalHeaderProps.icon}
                hideCloseButton={blockClose}
            >
                <AuthSteps modal key="auth-steps" />
            </GeneralModalContent>
        </div>
    )
}
